import axios from 'axios'
import { sdk } from '@service/http/axios'

export default class ChunkUploadService {
  constructor(options = {}) {
    this.state = options.state
    this.onComplete = options.onComplete
    this.onError = options.onError
    this.onSuccess = options.onSuccess

    this.currentFile = null
    this.chunks = []
    this.currentChunkIndex = 0
    this.chunkSize = 2 * 1024 * 1024
    this.totalChunks = 0
    this.uploadController = null
    this.fileId = null
    this.chunkRetries = {}
    this.maxRetries = 3
    this.onProgress = options.onProgress
  }

  resetUpload() {
    this.state.isUploading = false
    this.state.isCompleting = false
    this.state.progress = 0
    this.state.isPaused = false
    this.state.statusMessage = ''
    this.state.uploadResult = null
    this.state.failedChunks = []

    this.currentChunkIndex = 0
    this.chunkRetries = {}
    this.uploadController = null
  }
  notifyProgress() {
    if (this.onProgress) {
      this.onProgress({
        progress: this.state.progress,
        statusMessage: this.state.statusMessage,
        failedChunks: this.state.failedChunks,
        uploadResult: this.state.uploadResult,
      })
    }
  }
  async startUpload(file) {
    try {
      this.resetUpload()

      this.state.isUploading = true
      this.currentFile = file

      const fieldIdResponse = await sdk.get('upload/generate-field-id', {
        params: {
          fileSize: file.size,
        },
      })

      if (fieldIdResponse.data.status !== 'success') {
        throw new Error(
          fieldIdResponse.data.message || 'Failed to generate field ID'
        )
      }

      this.fileId = fieldIdResponse.data.fieldId
      this.chunkSize = fieldIdResponse.data.maxChunkSize
      this.maxRetries = fieldIdResponse.data.maxRetries

      this.createChunks(file)
      await this.uploadNextChunk()
    } catch (error) {
      this.setError('Tải lên thất bại: ' + error.message)
    }
  }

  createChunks(file) {
    this.chunks = []

    let start = 0

    while (start < file.size) {
      this.chunks.push(file.slice(start, start + this.chunkSize))
      start += this.chunkSize
    }

    this.totalChunks = this.chunks.length
    this.currentChunkIndex = 0
    this.chunkRetries = {}
  }

  async uploadNextChunk() {
    if (this.state.isPaused || this.currentChunkIndex >= this.totalChunks) {
      if (
        this.currentChunkIndex >= this.totalChunks &&
        this.state.isUploading &&
        !this.state.isCompleting
      ) {
        if (this.state.failedChunks.length > 0) {
          this.setError(
            `Tải lên thất bại: ${this.state.failedChunks.length} phần không thể tải lên.`
          )
          return
        }

        this.state.isCompleting = true
        await this.completeUpload()
      }

      return
    }

    const chunk = this.chunks[this.currentChunkIndex]

    const formData = new FormData()
    formData.append(
      'file',
      chunk,
      this.currentFile ? this.currentFile.name : ''
    )
    formData.append('fileId', this.fileId || '')
    formData.append('chunkIndex', String(this.currentChunkIndex))
    formData.append('totalChunks', String(this.totalChunks))
    formData.append(
      'retryCount',
      String(this.chunkRetries[this.currentChunkIndex] || 0)
    )

    try {
      this.uploadController = new AbortController()

      const response = await sdk.post('upload/chunk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        signal: this.uploadController.signal,
        onUploadProgress: (progressEvent) => {
          const chunkProgress =
            progressEvent.loaded / (progressEvent.total || chunk.size)

          this.state.progress = Math.round(
            ((this.currentChunkIndex + chunkProgress) / this.totalChunks) * 100
          )

          this.state.statusMessage =
            'Đang tải lên... ' + this.state.progress + '%'
          this.notifyProgress()
        },
      })

      if (response.data.status === 'success') {
        const failedIndex = this.state.failedChunks.indexOf(
          this.currentChunkIndex
        )

        if (failedIndex > -1) {
          this.state.failedChunks.splice(failedIndex, 1)
        }

        this.currentChunkIndex++

        await new Promise((resolve) => setTimeout(resolve, 500))
        await this.uploadNextChunk()
      }
    } catch (error) {
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        this.state.statusMessage = 'Đã tạm dừng'
        return
      }

      this.chunkRetries[this.currentChunkIndex] =
        (this.chunkRetries[this.currentChunkIndex] || 0) + 1

      if (this.chunkRetries[this.currentChunkIndex] < this.maxRetries) {
        this.state.statusMessage = `Đang thử lại phần ${this.currentChunkIndex +
          1}... (Lần thử ${this.chunkRetries[this.currentChunkIndex]})`

        await new Promise((resolve) => setTimeout(resolve, 1000))
        await this.uploadNextChunk()
        return
      }

      if (!this.state.failedChunks.includes(this.currentChunkIndex)) {
        this.state.failedChunks.push(this.currentChunkIndex)
      }

      this.setError(
        `Không thể tải lên phần ${this.currentChunkIndex + 1}: ${error.message}`
      )
    }
  }

  async completeUpload() {
    if (!this.state.isUploading || !this.state.isCompleting) return

    try {
      const response = await sdk.post('upload/complete', {
        fileId: this.fileId,
        fileName: this.currentFile ? this.currentFile.name : '',
        totalChunks: this.totalChunks,
      })

      this.state.uploadResult = {
        status: 'success',
        message: 'Tải lên thành công!',
        data: response.data.data,
      }

      if (this.onComplete) {
        await this.onComplete(response.data.data)
      }

      if (this.onSuccess) {
        this.onSuccess('Tệp đã được tải lên thành công')
      }
    } catch (error) {
      this.setError('Không thể hoàn thành tải lên: ' + error.message)
    } finally {
      this.state.isCompleting = false
      this.state.progress = 100
      this.state.statusMessage = 'Tải lên hoàn thành'
    }
  }

  pauseUpload() {
    if (this.uploadController) {
      this.uploadController.abort()
      this.state.isPaused = true
    }
  }

  resumeUpload() {
    this.state.isPaused = false
    this.uploadNextChunk()
  }

  async retryFailedUpload() {
    if (!this.currentFile) return

    await this.startUpload(this.currentFile)
  }

  async cancelUpload() {
    if (this.uploadController) {
      this.uploadController.abort()
    }

    if (this.fileId) {
      try {
        await sdk.post('upload/cleanup', {
          fileId: this.fileId,
        })
      } catch (error) {
        console.error('Failed to cleanup chunks:', error)
      }
    }

    this.resetUpload()
  }

  setError(message) {
    this.state.uploadResult = {
      status: 'error',
      message,
    }

    if (this.onError) {
      this.onError(message)
    }
  }
}
